import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Send, X, ExternalLink } from "lucide-react";
import JeaCard from "@/components/jea/JeaCard";
import JeaButton from "@/components/jea/JeaButton";
import JeaToggle from "@/components/jea/JeaToggle";
import Skeleton from "@/components/jea/Skeleton";
import { apiClient, formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

const EVENT_META = [
  ["upcoming_query", "Upcoming Query (7 days before)"],
  ["query_due_today", "Query Due Today"],
  ["query_results_available", "Query Results Available"],
  ["new_assets_found", "New Assets Found"],
  ["tier_change_confirmed", "Tier Change Confirmed"],
  ["billing_event", "Billing Event"],
  ["account_security_alert", "Account Security Alert"],
  ["bankruptcy_detected", "Bankruptcy Filing Detected"],
  ["recovery_opportunity_found", "Recovery Opportunity Found (Lookback)"],
  // Phase 9D — judgment alerts row (in-app only for now, email/SMS stubs).
  ["judgment_alert", "Judgment Deadline Alerts (Phase 9D)"],
];

const CHANNELS = ["email", "sms", "in_app"];
const CH_LABELS = { email: "Email", sms: "SMS", in_app: "In-App" };

const key = (e, c) => `${e}:${c}`;

// -------------------------------------------------------------------------
// Renewal Concierge sample digest modal
// -------------------------------------------------------------------------
const SampleDigestModal = ({ open, html, filename, generatedAt, onClose }) => {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[95] bg-black/60 backdrop-blur-[12px] flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="sample-digest-modal"
    >
      <div
        className="w-full max-w-[720px] max-h-[85vh] bg-bg-secondary border border-border-default rounded-[16px] p-6 shadow-elevated overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="jea-caption">Renewal Concierge · Preview</div>
            <h3 className="text-white font-bold text-[18px]">Sample Weekly Digest</h3>
            <div className="text-text-secondary text-[12px] mt-1">
              Generated {generatedAt || "—"}
              {filename && (
                <>
                  {" "}·{" "}
                  <span className="jea-mono text-text-muted">{filename}</span>
                </>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-text-secondary hover:text-white"
            data-testid="sample-digest-close"
          >
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 rounded-[8px] border border-border-default bg-white overflow-auto" data-testid="sample-digest-iframe-wrap">
          <iframe
            title="Renewal Concierge digest preview"
            srcDoc={html || "<p style='font-family:sans-serif;padding:24px;color:#555;'>No sample available — click 'Generate sample' to create one.</p>"}
            className="w-full h-[540px] block bg-white"
            sandbox=""
          />
        </div>
      </div>
    </div>
  );
};

const RenewalConciergePanel = ({ prefs, setPref, disabled }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalHtml, setModalHtml] = useState("");
  const [modalMeta, setModalMeta] = useState({ filename: "", generated_at: "" });
  const [generating, setGenerating] = useState(false);
  const emailEnabled = !!prefs[key("renewal_concierge_digest", "email")];

  const openLatest = async () => {
    try {
      const { data } = await apiClient.get("/notifications/digests/latest");
      const dg = data?.digest;
      if (!dg) {
        toast.info("No digest generated yet — click 'Generate sample' first.");
        return;
      }
      setModalHtml(dg.content_html || "");
      setModalMeta({ filename: dg.filename || "", generated_at: dg.generated_at || "" });
      setModalOpen(true);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const generateSample = async () => {
    setGenerating(true);
    try {
      await apiClient.post("/notifications/digests/generate-sample");
      const { data } = await apiClient.get("/notifications/digests/latest");
      const dg = data?.digest;
      if (dg) {
        setModalHtml(dg.content_html || "");
        setModalMeta({ filename: dg.filename || "", generated_at: dg.generated_at || "" });
        setModalOpen(true);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <JeaCard className="mt-6" data-testid="renewal-concierge-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-[10px] bg-accent-pink/15 border border-accent-pink/40 flex items-center justify-center shrink-0">
            <Sparkles size={16} className="text-accent-pink" />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-white font-bold text-[16px]">Renewal Concierge</h3>
              <span
                className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-accent-pink text-[#091424] tracking-[0.08em] uppercase"
                data-testid="renewal-concierge-badge"
              >
                PRO Add-On
              </span>
            </div>
            <p className="text-text-secondary text-[13px] max-w-[520px]">
              Weekly email digest summarizing your top 10 approaching deadlines
              — sorted by urgency and deep-linked into the Action Checklist. Sent
              every Monday 8:00 UTC (email delivery is stubbed until Brevo keys ship in Phase 10).
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-text-secondary text-[12px] jea-caption">Enable</span>
            <JeaToggle
              checked={emailEnabled}
              disabled={disabled}
              onChange={(v) => setPref("renewal_concierge_digest", "email", v)}
              data-testid="renewal-concierge-enable"
            />
          </div>
        </div>
      </div>
      <div className="mt-4 flex gap-2 flex-wrap">
        <JeaButton
          variant="outline-pink"
          onClick={generateSample}
          loading={generating}
          disabled={disabled}
          className="gap-2"
          data-testid="renewal-concierge-generate"
        >
          <Send size={13} /> Generate sample
        </JeaButton>
        <button
          type="button"
          onClick={openLatest}
          disabled={disabled}
          className="h-11 px-4 rounded-[8px] border border-border-default text-text-secondary hover:text-white hover:bg-bg-tertiary text-[13px] font-semibold inline-flex items-center gap-2 disabled:opacity-40"
          data-testid="renewal-concierge-view-sample"
        >
          <ExternalLink size={13} /> View sample digest
        </button>
      </div>
      <SampleDigestModal
        open={modalOpen}
        html={modalHtml}
        filename={modalMeta.filename}
        generatedAt={modalMeta.generated_at}
        onClose={() => setModalOpen(false)}
      />
    </JeaCard>
  );
};

const RenewalConciergeProGate = () => (
  <JeaCard className="mt-6 border-dashed opacity-80" data-testid="renewal-concierge-gate">
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-[10px] bg-accent-pink/10 border border-accent-pink/30 flex items-center justify-center shrink-0">
        <Sparkles size={16} className="text-accent-pink" />
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <h3 className="text-white font-bold text-[16px]">Renewal Concierge</h3>
          <span
            className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-accent-pink/20 text-accent-pink tracking-[0.08em] uppercase"
            data-testid="renewal-concierge-badge"
          >
            PRO Add-On
          </span>
        </div>
        <p className="text-text-secondary text-[13px] max-w-[520px]">
          A weekly email digest of your top 10 approaching deadlines with one-click links into the Action Checklist. Available on PRO plans.
        </p>
      </div>
      <a
        href="/billing?open=change-plan&target=pro-monthly"
        className="h-10 px-4 rounded-[8px] bg-accent-pink text-[#091424] text-[13px] font-bold inline-flex items-center"
        data-testid="renewal-concierge-upgrade"
      >
        Upgrade to PRO
      </a>
    </div>
  </JeaCard>
);

// -------------------------------------------------------------------------
// Main panel
// -------------------------------------------------------------------------
const NotificationPrefsPanel = () => {
  const { user } = useAuth();
  const isPro = user?.account_type === "pro";

  const [prefs, setPrefs] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [initial, setInitial] = useState({});

  const load = async () => {
    try {
      const { data } = await apiClient.get("/notifications/preferences");
      const map = {};
      (data.preferences || []).forEach((p) => {
        map[key(p.event_type, p.channel)] = !!p.enabled;
      });
      setPrefs(map);
      setInitial(map);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const dirty = useMemo(() =>
    Object.keys(prefs).some((k) => prefs[k] !== initial[k]),
    [prefs, initial]);

  const set = (evt, ch, val) => {
    setPrefs((p) => ({ ...p, [key(evt, ch)]: val }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const changed = Object.keys(prefs)
        .filter((k) => prefs[k] !== initial[k])
        .map((k) => {
          const [event_type, channel] = k.split(":");
          return { event_type, channel, enabled: prefs[k] };
        });
      await apiClient.patch("/notifications/preferences", { preferences: changed });
      toast.success("Preferences saved.");
      setInitial(prefs);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <JeaCard data-testid="notif-prefs-panel">
        <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
          <div>
            <h2 className="text-white font-bold text-[18px]">Notification Preferences</h2>
            <p className="text-text-secondary text-[13px] mt-1 max-w-[560px]">
              Control which notifications you receive per event and per channel.
            </p>
          </div>
          <JeaButton onClick={save} disabled={!dirty} loading={saving} data-testid="notif-prefs-save">
            Save Changes
          </JeaButton>
        </div>

        {loading ? (
          <Skeleton className="h-64" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="notif-prefs-table">
              <thead>
                <tr className="border-b border-border-default">
                  <th className="jea-caption text-text-secondary p-3 text-left">Event Type</th>
                  {CHANNELS.map((c) => (
                    <th key={c} className="jea-caption text-text-secondary p-3 text-center w-[100px]">
                      {CH_LABELS[c]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {EVENT_META.map(([evt, label]) => (
                  <tr key={evt} className={cn(
                    "border-b border-border-default hover:bg-bg-tertiary transition-colors",
                    evt === "judgment_alert" && "bg-accent-pink/5",
                  )} data-testid={`notif-row-${evt}`}>
                    <td className="p-3 text-white text-[14px]">
                      {label}
                      {evt === "judgment_alert" && (
                        <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-accent-pink/20 text-accent-pink tracking-[0.06em] uppercase">
                          Auto
                        </span>
                      )}
                    </td>
                    {CHANNELS.map((ch) => (
                      <td key={ch} className="p-3 text-center">
                        <JeaToggle
                          checked={!!prefs[key(evt, ch)]}
                          onChange={(v) => set(evt, ch, v)}
                          data-testid={`notif-toggle-${evt}-${ch}`}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-text-secondary text-[13px] mt-4">
          Email and SMS delivery are stubbed until provider credentials (Brevo, Twilio) are configured.
          Toggle state is saved and will take effect once live.
        </p>
      </JeaCard>

      {isPro ? (
        <RenewalConciergePanel
          prefs={prefs}
          setPref={set}
          disabled={loading}
        />
      ) : (
        <RenewalConciergeProGate />
      )}
    </div>
  );
};

export default NotificationPrefsPanel;
